import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import {
  Calendar, Clock, MapPin, Users, ArrowLeft, Ticket,
  CheckCircle, Loader2, ImageIcon, Heart, Phone, ShoppingBag,
  Package, DollarSign, Gift, HandHeart
} from 'lucide-react';
import axios from 'axios';
import EventFundraisingBar from './components/EventFundraisingBar';
import EventItemTracker from './components/EventItemTracker';
import { TransparencyNotice, TransparencyCheckbox } from './components/TransparencyNotice';
import { MerchTransparencyNotice } from './components/MerchTransparencyNotice';
import ImageLightbox from '../components/ImageLightbox';
import PhotoLightbox from '../components/PhotoLightbox';
import { isValidEmail, getEmailError } from '../utils/emailValidation';
import { useImageDisplayConfig } from '../hooks/useSiteContent';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/* ========== RSVP Type Card ========== */
const RsvpCard = ({ type, icon, title, description, selected, onClick, children }) => (
  <div
    onClick={onClick}
    className={`border-2 rounded-xl p-5 cursor-pointer transition-all ${
      selected
        ? 'border-[#BA1826] bg-[#FEF2F2] shadow-sm'
        : 'border-[#E5E7EB] bg-white hover:border-[#BA1826]/30 hover:shadow-sm'
    }`}
    data-testid={`rsvp-card-${type}`}
  >
    <div className="flex items-start gap-3">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
        selected ? 'bg-[#BA1826] text-white' : 'bg-[#F1F5F9] text-[#4B5563]'
      }`}>
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="font-heading text-sm font-bold text-[#0A0A0A]">{title}</h4>
        <p className="text-xs text-[#4B5563] mt-0.5">{description}</p>
      </div>
      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
        selected ? 'border-[#BA1826] bg-[#BA1826]' : 'border-[#D1D5DB]'
      }`}>
        {selected && <div className="w-2 h-2 rounded-full bg-white" />}
      </div>
    </div>
    {selected && children && (
      <div className="mt-4 pt-4 border-t border-[#BA1826]/10" onClick={e => e.stopPropagation()}>
        {children}
      </div>
    )}
  </div>
);

/* ========== Item Selection List (grouped by category) ========== */
const ItemSelectionList = ({ items, selectedItem, setSelectedItem, itemQuantity, setItemQuantity, radioName = 'selected_item' }) => {
  // Group items by category
  const categories = {};
  const uncategorized = [];
  items.forEach((item, idx) => {
    const cat = item.category || '';
    const itemWithIdx = { ...item, _idx: idx };
    if (cat) {
      if (!categories[cat]) categories[cat] = [];
      categories[cat].push(itemWithIdx);
    } else {
      uncategorized.push(itemWithIdx);
    }
  });
  const categoryOrder = ['Baby Supplies', 'Hygiene', 'Food', 'Clothing', 'Other'];
  const sortedCategories = Object.keys(categories).sort((a, b) => {
    const ai = categoryOrder.indexOf(a);
    const bi = categoryOrder.indexOf(b);
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });

  const renderItem = (item) => {
    const remaining = item.goal_qty > 0 ? Math.max(0, item.goal_qty - (item.committed_qty || 0)) : null;
    const isFulfilled = remaining !== null && remaining <= 0;
    return (
      <label key={item._idx}
        className={`flex items-center gap-3 p-3 border rounded-lg transition-colors text-sm ${
          isFulfilled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
        } ${selectedItem === item.name ? 'border-[#BA1826] bg-[#FEF2F2]' : 'border-[#E5E7EB] hover:border-[#BA1826]/30'}`}
        data-testid={`item-option-${item._idx}`}>
        <input type="radio" name={radioName} value={item.name} disabled={isFulfilled}
          checked={selectedItem === item.name}
          onChange={(e) => { setSelectedItem(e.target.value); setItemQuantity(1); }}
          className="accent-[#BA1826]" />
        <span className="flex-1">
          {item.name}
          {remaining !== null && !isFulfilled && (
            <span className="text-[#BA1826] font-semibold ml-1">— {remaining} left</span>
          )}
        </span>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {item.high_priority && !isFulfilled && (
            <span className="text-[9px] font-bold bg-red-100 text-red-700 px-1.5 py-0.5 rounded">High Priority</span>
          )}
          {isFulfilled && <span className="text-[10px] font-bold bg-green-100 text-green-700 px-2 py-0.5 rounded">Fully Claimed</span>}
        </div>
      </label>
    );
  };

  const selItem = items.find(i => i.name === selectedItem);
  const maxQty = selItem && selItem.goal_qty > 0 ? Math.max(1, selItem.goal_qty - (selItem.committed_qty || 0)) : 50;

  return (
    <div>
      <p className="text-[11px] text-[#4B5563] mb-3 italic">
        Please select one bulk item from the list below. Items are limited based on current needs.
      </p>
      <div className="space-y-4">
        {sortedCategories.map(cat => (
          <div key={cat}>
            <p className="text-[10px] font-bold text-[#4B5563] uppercase tracking-wide mb-1.5">{cat}</p>
            <div className="space-y-2">{categories[cat].map(renderItem)}</div>
          </div>
        ))}
        {uncategorized.length > 0 && (
          <div>
            {sortedCategories.length > 0 && <p className="text-[10px] font-bold text-[#4B5563] uppercase tracking-wide mb-1.5">Other Items</p>}
            <div className="space-y-2">{uncategorized.map(renderItem)}</div>
          </div>
        )}
      </div>
      {selectedItem && (
        <div className="flex items-center gap-3 mt-3">
          <label className="text-xs text-[#4B5563]">Quantity:</label>
          <input type="number" min="1" max={maxQty} value={Math.min(itemQuantity, maxQty)}
            onChange={(e) => setItemQuantity(Math.max(1, Math.min(maxQty, parseInt(e.target.value) || 1)))}
            className="w-20 px-3 py-2 border border-[#E5E7EB] rounded-lg text-sm text-center focus:border-[#BA1826] focus:outline-none"
            data-testid="item-quantity-input" />
          {selItem && selItem.goal_qty > 0 && <span className="text-[10px] text-[#9CA3AF]">max {maxQty}</span>}
        </div>
      )}
    </div>
  );
};

const SingleEventPage = () => {
  const { eventId } = useParams();
  const [searchParams] = useSearchParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rsvpSuccess, setRsvpSuccess] = useState(false);
  const [shopItems, setShopItems] = useState([]);
  const [entryId, setEntryId] = useState(null);
  const imgConfig = useImageDisplayConfig();

  // Form state
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [rsvpType, setRsvpType] = useState('free');
  const [donationAmount, setDonationAmount] = useState(25);
  const [selectedItem, setSelectedItem] = useState('');
  const [itemQuantity, setItemQuantity] = useState(1);
  const [transparencyAcknowledged, setTransparencyAcknowledged] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [confirmationMsg, setConfirmationMsg] = useState('');
  const [lightboxImg, setLightboxImg] = useState(null);
  // Phase D — unified PhotoLightbox for the event's curated photo story
  // (flyer + event_photos). We keep `lightboxImg` for merchandise item
  // images (out-of-scope, single-shot images).
  const [flyerLightboxOpen, setFlyerLightboxOpen] = useState(false);

  const fetchEvent = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/public/events/${eventId}`);
      setEvent(res.data);
      try {
        const shopRes = await axios.get(`${BACKEND_URL}/api/public/shop/items?event_id=${eventId}`);
        setShopItems(shopRes.data.filter(i => i.event_id === eventId));
      } catch {}
    } catch {
      setEvent(null);
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    fetchEvent();
    if (searchParams.get('rsvp') === 'success') {
      setRsvpSuccess(true);
    }
  }, [fetchEvent, searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || submitting) return;
    const emailErr = getEmailError(email);
    if (emailErr) { alert(emailErr); return; }
    setSubmitting(true);
    try {
      const payload = {
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim(),
        ticket_quantity: 1,
        rsvp_type: rsvpType,
        donation_amount: (rsvpType === 'donation' || rsvpType === 'donation_item') ? donationAmount : 0,
        item_commitments: (rsvpType === 'item' || rsvpType === 'donation_item') && selectedItem ? [
          { item_name: selectedItem, quantity: itemQuantity }
        ] : [],
      };
      const res = await axios.post(`${BACKEND_URL}/api/public/events/${eventId}/rsvp`, payload);
      if (res.data.type === 'paid' && res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
        return;
      }
      if (res.data.already_registered) {
        alert("You've already RSVP'd for this event! Check your email for your entry pass.");
        setSubmitting(false);
        return;
      }
      setConfirmationMsg(res.data.message || '');
      setRsvpSuccess(true);
      if (res.data.entry_id) setEntryId(res.data.entry_id);
      setName(''); setEmail(''); setPhone('');
      fetchEvent();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Error submitting RSVP';
      alert(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#BA1826]" />
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6 text-center">
        <p className="text-lg font-semibold text-[#0A0A0A] mb-2">Event not found</p>
        <p className="text-sm text-[#4B5563] mb-4">This event may have been removed or is not yet published.</p>
        <Link to="/events" className="btn-primary text-sm">
          <ArrowLeft className="w-4 h-4" /> Back to Events
        </Link>
      </div>
    );
  }

  const isPaid = event.ticket_type === 'paid' && event.ticket_price > 0;
  const isCompleted = event.status === 'completed';
  const maxCap = event.max_capacity || 0;
  const isFull = maxCap > 0 && event.rsvp_count >= maxCap;
  const canRsvp = event.rsvp_enabled && !isCompleted && !isFull;
  const needsPayment = rsvpType === 'donation' || rsvpType === 'donation_item' || rsvpType === 'vip' || isPaid;

  // Build RSVP options based on event config
  const suggestedItems = (event.suggested_items?.items || []).filter(i => i.enabled !== false && i.name);
  const hasSuggestedItems = event.suggested_items?.enabled && suggestedItems.length > 0;
  const hasDonation = event.donation_rsvp_enabled;

  const getRsvpButtonText = () => {
    if (submitting) return 'Processing...';
    if (rsvpType === 'item' && !selectedItem) return 'Select an Item to RSVP';
    if (rsvpType === 'donation_item' && !selectedItem) return 'Select an Item to Continue';
    if (rsvpType === 'donation') return `RSVP with $${donationAmount} Donation`;
    if (rsvpType === 'donation_item') return `RSVP — $${donationAmount} Donation + Item`;
    if (rsvpType === 'vip') return `Become a VIP ($${event.vip_price || 50})`;
    if (isPaid) return `Get Ticket — $${event.ticket_price}`;
    return 'Confirm RSVP';
  };

  return (
    <>
    <div>
      {/* Back link */}
      <div className="px-6 py-3">
        <Link to="/events" className="text-xs text-[#4B5563] hover:text-[#BA1826] flex items-center gap-1" data-testid="back-to-events">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to Events & Impact
        </Link>
      </div>

      {/* Hero / Flyer */}
      <section data-testid="event-hero">
        {event.flyer_image ? (
          <div className="w-full max-h-[400px] overflow-hidden cursor-pointer" onClick={() => setFlyerLightboxOpen(true)} data-testid="flyer-clickable">
            <img src={event.flyer_image} alt={event.title} className="w-full h-full object-cover" />
          </div>
        ) : imgConfig.hide_empty_event_images ? null : (
          <div className="h-48 bg-[#F8F9FA] border-b border-dashed border-[#E2E8F0] flex flex-col items-center justify-center p-6">
            <ImageIcon className="w-8 h-8 text-[#CBD5E1] mb-2" />
            <p className="text-sm text-[#94A3B8]">Event flyer coming soon</p>
          </div>
        )}
      </section>

      {/* Event Details */}
      <section className="px-6 py-8" data-testid="event-details">
        <div className="flex flex-wrap gap-2 mb-3">
          {isCompleted && <span className="text-[10px] font-semibold bg-gray-100 text-gray-600 px-2 py-0.5 rounded">Completed</span>}
          {event.is_active && !isCompleted && <span className="text-[10px] font-semibold bg-green-100 text-green-700 px-2 py-0.5 rounded">Active</span>}
          {isPaid && <span className="text-[10px] font-semibold bg-blue-100 text-blue-700 px-2 py-0.5 rounded">${event.ticket_price}</span>}
          {!isPaid && !isCompleted && <span className="text-[10px] font-semibold bg-green-100 text-green-700 px-2 py-0.5 rounded">Free</span>}
        </div>

        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#0A0A0A] mb-4" data-testid="event-title">
          {event.title}
        </h1>

        <div className="flex flex-wrap gap-4 text-sm text-[#4B5563] mb-5">
          {event.date && <span className="flex items-center gap-1.5"><Calendar className="w-4 h-4 text-[#BA1826]" /> {event.date}</span>}
          {event.time && <span className="flex items-center gap-1.5"><Clock className="w-4 h-4 text-[#BA1826]" /> {event.time}</span>}
          {event.location && <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4 text-[#BA1826]" /> {event.location}</span>}
        </div>

        {maxCap > 0 && (
          <div className="flex items-center gap-2 text-xs text-[#4B5563] mb-4" data-testid="event-capacity">
            <Users className="w-3.5 h-3.5 text-[#BA1826]" />
            <span>{event.rsvp_count || 0} / {maxCap} spots filled</span>
          </div>
        )}

        {event.description && (
          <div className="prose prose-sm max-w-none text-[#4B5563] leading-relaxed mb-6" data-testid="event-description">
            <p className="whitespace-pre-wrap">{event.description}</p>
          </div>
        )}
      </section>

      {/* Fundraising + Item Progress */}
      {event.fundraising?.enabled && (
        <section className="px-6 pb-6"><EventFundraisingBar event={event} /></section>
      )}
      {event.event_items?.length > 0 && (
        <section className="px-6 pb-6"><EventItemTracker event={event} /></section>
      )}

      {/* Event Merch */}
      {shopItems.length > 0 && (
        <section className="px-6 pb-6" data-testid="event-shop-section">
          <h3 className="font-heading text-lg font-bold text-[#0A0A0A] mb-3 flex items-center gap-2">
            <ShoppingBag className="w-5 h-5 text-[#BA1826]" /> Event Merch
          </h3>
          <div className="grid sm:grid-cols-2 gap-4">
            {shopItems.map(item => {
              const isSoldOut = item.is_sold_out;
              return (
                <div key={item.id} className={`bg-white border border-[#E5E7EB] rounded-xl overflow-hidden ${isSoldOut ? 'opacity-60' : ''}`}
                  data-testid={`event-shop-item-${item.id}`}>
                  <div className="h-36 bg-[#F1F5F9] relative flex cursor-pointer" onClick={() => item.image && setLightboxImg(item.image)} data-testid={`merch-img-clickable-${item.id}`}>
                    {item.image ? (
                      <>
                        <img src={item.image} alt={item.name} className={`${item.image_2 ? 'w-1/2' : 'w-full'} h-full object-cover`} />
                        {item.image_2 && <img src={item.image_2} alt="" className="w-1/2 h-full object-cover border-l border-[#E5E7EB]" />}
                      </>
                    ) : imgConfig.hide_empty_product_images ? null : (
                      <div className="w-full h-full flex items-center justify-center"><Package className="w-8 h-8 text-[#D1D5DB]" /></div>
                    )}
                    {isSoldOut && (
                      <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                        <span className="bg-red-600 text-white text-xs font-bold px-3 py-1 rounded">SOLD OUT</span>
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <h4 className="font-heading text-sm font-bold text-[#0A0A0A] mb-1">{item.name}</h4>
                    <div className="flex items-center justify-between">
                      <span className="font-heading text-base font-bold text-[#BA1826]">${item.price.toFixed(2)}</span>
                      <Link to="/shop" className="text-xs font-semibold bg-[#BA1826] text-white px-3 py-1.5 rounded-lg hover:bg-[#A41623] flex items-center gap-1"
                        data-testid={`buy-event-item-${item.id}`}>
                        <ShoppingBag className="w-3 h-3" /> Buy Now
                      </Link>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-3">
            <MerchTransparencyNotice variant="event" />
          </div>
        </section>
      )}

      {/* ====== RSVP SECTION ====== */}
      {canRsvp && (
        <section className="px-6 pb-10" data-testid="rsvp-section">
          {rsvpSuccess ? (
            /* ====== SUCCESS CONFIRMATION ====== */
            <div className="bg-green-50 border border-green-200 rounded-xl p-8 text-center" data-testid="rsvp-confirmation">
              <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-4" />
              <h3 className="font-heading text-2xl font-bold text-green-800 mb-3">Thank You!</h3>
              <p className="text-sm text-green-700 leading-relaxed max-w-sm mx-auto">
                Your RSVP has been confirmed. Check your email for your entry pass with barcode and QR code.
              </p>
            </div>
          ) : (
            /* ====== RSVP FORM ====== */
            <div className="bg-white border border-[#E5E7EB] rounded-xl p-6" data-testid="rsvp-form-container">
              <h3 className="font-heading text-lg font-bold text-[#0A0A0A] mb-1 flex items-center gap-2">
                <Ticket className="w-5 h-5 text-[#BA1826]" />
                {isPaid ? 'Get Your Ticket' : 'Register for This Event'}
              </h3>
              <p className="text-xs text-[#4B5563] mb-5">Choose how you'd like to attend</p>

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Name + Email */}
                <div className="grid sm:grid-cols-2 gap-3">
                  <input type="text" placeholder="Full name *" value={name} onChange={(e) => setName(e.target.value)}
                    className="w-full px-3 py-3 border border-[#E5E7EB] rounded-lg text-sm focus:border-[#BA1826] focus:outline-none"
                    required data-testid="rsvp-name-input" />
                  <input type="email" placeholder="Email address *" value={email} onChange={(e) => setEmail(e.target.value)}
                    className={`w-full px-3 py-3 border rounded-lg text-sm focus:outline-none ${email && !isValidEmail(email) ? 'border-red-400 focus:border-red-500' : 'border-[#E5E7EB] focus:border-[#BA1826]'}`}
                    required data-testid="rsvp-email-input" />
                  {email && !isValidEmail(email) && (
                    <p className="text-[10px] text-red-500 -mt-1">{getEmailError(email)}</p>
                  )}
                </div>
                <input type="tel" placeholder="Phone number (optional)" value={phone} onChange={(e) => setPhone(e.target.value)}
                  className="w-full px-3 py-3 border border-[#E5E7EB] rounded-lg text-sm focus:border-[#BA1826] focus:outline-none"
                  data-testid="rsvp-phone-input" />

                {/* ====== RSVP TYPE CARDS ====== */}
                <div className="space-y-3" data-testid="rsvp-type-cards">
                  {/* Card 1: Free RSVP */}
                  <RsvpCard
                    type="free"
                    icon={<Ticket className="w-5 h-5" />}
                    title={isPaid ? `Standard Ticket ($${event.ticket_price})` : 'Free RSVP'}
                    description="Attend the event — no donation or items required"
                    selected={rsvpType === 'free'}
                    onClick={() => setRsvpType('free')}
                  />

                  {/* Card 2: Donation RSVP */}
                  {hasDonation && (
                    <RsvpCard
                      type="donation"
                      icon={<DollarSign className="w-5 h-5" />}
                      title="Donation RSVP"
                      description="Attend and support with a financial contribution"
                      selected={rsvpType === 'donation'}
                      onClick={() => setRsvpType('donation')}
                    >
                      <div data-testid="donation-amount-field">
                        <label className="text-xs font-medium text-[#0A0A0A] mb-2 block">Choose an amount</label>
                        <div className="flex flex-wrap gap-2 mb-2" data-testid="donation-presets">
                          {(event.donation_presets || [10, 25, 50, 100]).map(amount => (
                            <button key={amount} type="button" onClick={() => setDonationAmount(amount)}
                              className={`px-4 py-2.5 rounded-xl text-sm font-semibold border transition-colors ${
                                donationAmount === amount
                                  ? 'bg-[#BA1826] text-white border-[#BA1826]'
                                  : 'bg-white text-[#0A0A0A] border-[#E5E7EB] hover:border-[#BA1826]/40'
                              }`}
                              data-testid={`preset-amount-${amount}`}>
                              ${amount}
                            </button>
                          ))}
                        </div>
                        {event.donation_custom_enabled !== false && (
                          <input type="number" min="5" step="1" value={donationAmount}
                            onChange={(e) => setDonationAmount(parseFloat(e.target.value) || 0)}
                            placeholder="Custom amount"
                            className="w-full px-3 py-2.5 border border-[#E5E7EB] rounded-lg text-sm focus:border-[#BA1826] focus:outline-none"
                            data-testid="custom-donation-input" />
                        )}
                      </div>
                    </RsvpCard>
                  )}

                  {/* Card 3: Bring a Bulk Item */}
                  {hasSuggestedItems && (
                    <RsvpCard
                      type="item"
                      icon={<Gift className="w-5 h-5" />}
                      title="Bring a Bulk Item"
                      description="Attend and contribute supplies for those in need"
                      selected={rsvpType === 'item'}
                      onClick={() => setRsvpType('item')}
                    >
                      <div data-testid="item-selection">
                        <ItemSelectionList
                          items={suggestedItems}
                          selectedItem={selectedItem}
                          setSelectedItem={setSelectedItem}
                          itemQuantity={itemQuantity}
                          setItemQuantity={setItemQuantity}
                          radioName="selected_item"
                        />
                      </div>
                    </RsvpCard>
                  )}

                  {/* Card 4: Donation + Item */}
                  {hasDonation && hasSuggestedItems && (
                    <RsvpCard
                      type="donation_item"
                      icon={<HandHeart className="w-5 h-5" />}
                      title="Donate + Bring Item"
                      description="Make a financial contribution and bring supplies"
                      selected={rsvpType === 'donation_item'}
                      onClick={() => setRsvpType('donation_item')}
                    >
                      <div className="space-y-4">
                        {/* Donation */}
                        <div data-testid="combo-donation">
                          <label className="text-xs font-medium text-[#0A0A0A] mb-2 block">Donation amount</label>
                          <div className="flex flex-wrap gap-2 mb-2">
                            {(event.donation_presets || [10, 25, 50, 100]).map(amount => (
                              <button key={amount} type="button" onClick={() => setDonationAmount(amount)}
                                className={`px-3 py-2 rounded-lg text-xs font-semibold border transition-colors ${
                                  donationAmount === amount
                                    ? 'bg-[#BA1826] text-white border-[#BA1826]'
                                    : 'bg-white text-[#0A0A0A] border-[#E5E7EB] hover:border-[#BA1826]/40'
                                }`}>
                                ${amount}
                              </button>
                            ))}
                          </div>
                          {event.donation_custom_enabled !== false && (
                            <input type="number" min="5" step="1" value={donationAmount}
                              onChange={(e) => setDonationAmount(parseFloat(e.target.value) || 0)}
                              className="w-full px-3 py-2 border border-[#E5E7EB] rounded-lg text-sm focus:border-[#BA1826] focus:outline-none" />
                          )}
                        </div>
                        {/* Item */}
                        <div data-testid="combo-item">
                          <ItemSelectionList
                            items={suggestedItems}
                            selectedItem={selectedItem}
                            setSelectedItem={setSelectedItem}
                            itemQuantity={itemQuantity}
                            setItemQuantity={setItemQuantity}
                            radioName="combo_item"
                          />
                        </div>
                      </div>
                    </RsvpCard>
                  )}

                  {/* VIP Option */}
                  {event.vip_rsvp_enabled && (
                    <RsvpCard
                      type="vip"
                      icon={<Heart className="w-5 h-5" />}
                      title={`VIP Supporter ($${event.vip_price || 50})`}
                      description="Premium support — priority seating and special recognition"
                      selected={rsvpType === 'vip'}
                      onClick={() => setRsvpType('vip')}
                    />
                  )}
                </div>

                {/* Transparency Notice */}
                {needsPayment && (
                  <>
                    <TransparencyNotice />
                    <TransparencyCheckbox
                      checked={transparencyAcknowledged}
                      onChange={(e) => setTransparencyAcknowledged(e.target.checked)}
                    />
                  </>
                )}

                {/* Submit */}
                <button type="submit"
                  disabled={submitting || !name.trim() || !email.trim() || (needsPayment && !transparencyAcknowledged) || ((rsvpType === 'item' || rsvpType === 'donation_item') && !selectedItem)}
                  className="w-full btn-primary text-sm py-3.5 disabled:opacity-40 mt-2 flex items-center justify-center gap-2"
                  data-testid="rsvp-submit-btn">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Ticket className="w-4 h-4" />}
                  {getRsvpButtonText()}
                </button>
                {(rsvpType === 'item' || rsvpType === 'donation_item') && !selectedItem && (
                  <p className="text-xs text-[#BA1826] text-center mt-1" data-testid="item-required-hint">Please select an item above before confirming your RSVP.</p>
                )}
              </form>
            </div>
          )}
        </section>
      )}

      {/* Full capacity */}
      {isFull && !isCompleted && (
        <section className="px-6 pb-10">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <p className="text-sm font-semibold text-red-700">This event has reached capacity.</p>
            <p className="text-xs text-red-600 mt-1">RSVP is currently closed.</p>
          </div>
        </section>
      )}
    </div>
    {/* Phase D: unified PhotoLightbox for the event photo story.
        Combines flyer + curated event_photos so users can prev/next
        through the full set. `object-contain`, mobile-safe height. */}
    <PhotoLightbox
      photos={[event.flyer_image, ...(event.event_photos || [])].filter(Boolean)}
      open={flyerLightboxOpen}
      startIndex={0}
      title={event.title}
      onClose={() => setFlyerLightboxOpen(false)}
    />
    {lightboxImg && <ImageLightbox src={lightboxImg} alt="Full size" onClose={() => setLightboxImg(null)} />}
    </>
  );
};

export default SingleEventPage;
